<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import LilithEffectRecovery from './LilithEffectRecovery.vue';
import {
  createLilithCustomTool,
  deleteLilithCustomTool,
  getLilithCustomTools,
  testLilithCustomTool,
  updateLilithCustomTool,
} from 'dashboard/api/lilithCustomTools';

const props = defineProps({
  canManage: {
    type: Boolean,
    default: false,
  },
});

const { accountId } = useAccount();
const { t } = useI18n();

const HTTP_METHODS = Object.freeze(['GET', 'POST']);
const PARAM_TYPES = Object.freeze(['string', 'integer', 'number', 'boolean']);
const ENDPOINT_PLACEHOLDER = 'https://api.example.com/orders/{{ order_id }}';

const tools = ref([]);
const loading = ref(false);
const saving = ref(false);
const testing = ref(false);
const formOpen = ref(false);
const formMode = ref('create');
const editingId = ref('');
const deleteArmed = ref('');
const testResult = ref(null);

const emptyForm = () => ({
  title: '',
  description: '',
  endpointUrl: '',
  httpMethod: 'GET',
  requestTemplate: '',
  responseTemplate: '',
  authType: 'none',
  bearerToken: '',
  basicUsername: '',
  basicPassword: '',
  apiKeyName: '',
  apiKeyValue: '',
  enabled: true,
  params: [],
});

const form = reactive(emptyForm());

const resetForm = () => {
  Object.assign(form, emptyForm());
  testResult.value = null;
};

const load = async () => {
  loading.value = true;
  deleteArmed.value = '';
  try {
    const response = await getLilithCustomTools(accountId.value);
    tools.value = response.data?.payload || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.TOOLS.ERROR')
    );
  } finally {
    loading.value = false;
  }
};

const toggle = async tool => {
  if (!props.canManage || saving.value) return;
  saving.value = true;
  try {
    await updateLilithCustomTool(accountId.value, tool.id, {
      enabled: !tool.enabled,
    });
    tool.enabled = !tool.enabled;
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TEAM_LILITH.TOOLS.ERROR'));
  } finally {
    saving.value = false;
  }
};
const openCreate = () => {
  if (!props.canManage || saving.value) return;
  resetForm();
  formMode.value = 'create';
  editingId.value = '';
  formOpen.value = true;
};

const openEdit = tool => {
  if (!props.canManage || saving.value) return;
  resetForm();
  const auth = tool.auth_config || {};
  Object.assign(form, {
    title: tool.title || '',
    description: tool.description || '',
    endpointUrl: tool.endpoint_url || '',
    httpMethod: tool.http_method || 'GET',
    requestTemplate: tool.request_template || '',
    responseTemplate: tool.response_template || '',
    authType: tool.auth_type || 'none',
    bearerToken: auth.token || '',
    basicUsername: auth.username || '',
    basicPassword: auth.password || '',
    apiKeyName: auth.name || '',
    apiKeyValue: auth.key || '',
    enabled: tool.enabled === true,
    params: (tool.param_schema || []).map(item => ({
      name: item.name || '',
      type: item.type || 'string',
      description: item.description || '',
      required: item.required === true,
    })),
  });
  formMode.value = 'edit';
  editingId.value = String(tool.id || '');
  formOpen.value = true;
};

const closeForm = () => {
  if (saving.value || testing.value) return;
  formOpen.value = false;
  editingId.value = '';
  resetForm();
};

const addParam = () => {
  form.params.push({
    name: '',
    type: 'string',
    description: '',
    required: true,
  });
};

const removeParam = index => {
  form.params.splice(index, 1);
};

const authConfig = computed(() => {
  if (form.authType === 'bearer') {
    return { token: form.bearerToken };
  }
  if (form.authType === 'basic') {
    return {
      username: form.basicUsername,
      password: form.basicPassword,
    };
  }
  if (form.authType === 'api_key') {
    return {
      name: form.apiKeyName,
      key: form.apiKeyValue,
    };
  }
  return {};
});

const authValid = computed(() => {
  if (form.authType === 'bearer') return Boolean(form.bearerToken);
  if (form.authType === 'basic') {
    return Boolean(form.basicUsername && form.basicPassword);
  }
  if (form.authType === 'api_key') {
    return Boolean(form.apiKeyName && form.apiKeyValue);
  }
  return true;
});

const paramsValid = computed(() => {
  const names = new Set();
  return form.params.every(param => {
    const name = param.name.trim();
    const description = param.description.trim();
    if (!name || !description || names.has(name)) return false;
    if (!/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(name)) return false;
    names.add(name);
    return ['string', 'integer', 'number', 'boolean'].includes(param.type);
  });
});

const formValid = computed(
  () =>
    form.title.trim().length > 0 &&
    form.title.trim().length <= 55 &&
    form.endpointUrl.trim().length > 0 &&
    authValid.value &&
    paramsValid.value
);

const canTest = computed(
  () =>
    props.canManage &&
    form.endpointUrl.trim().length > 0 &&
    !form.endpointUrl.includes('{{') &&
    !form.requestTemplate.trim() &&
    !testing.value
);

const softLimitWarning = computed(() => tools.value.length > 10);
const atHardLimit = computed(
  () => formMode.value === 'create' && tools.value.length >= 15
);

const payload = () => ({
  title: form.title.trim(),
  description: form.description.trim() || null,
  endpoint_url: form.endpointUrl.trim(),
  http_method: form.httpMethod,
  request_template:
    form.httpMethod === 'POST' && form.requestTemplate.trim()
      ? form.requestTemplate
      : null,
  response_template: form.responseTemplate.trim()
    ? form.responseTemplate
    : null,
  auth_type: form.authType,
  auth_config: authConfig.value,
  param_schema: form.params.map(param => ({
    name: param.name.trim(),
    type: param.type,
    description: param.description.trim(),
    required: param.required === true,
  })),
  enabled: form.enabled === true,
});

const save = async () => {
  if (
    !props.canManage ||
    !formValid.value ||
    atHardLimit.value ||
    saving.value
  ) {
    return;
  }
  saving.value = true;
  try {
    const data = payload();
    if (formMode.value === 'edit' && editingId.value) {
      await updateLilithCustomTool(accountId.value, editingId.value, data);
    } else {
      await createLilithCustomTool(accountId.value, data);
    }
    await load();
    formOpen.value = false;
    editingId.value = '';
    resetForm();
    useAlert(t('TEAM_LILITH.TOOLS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.TOOLS.ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const runTest = async () => {
  if (!canTest.value) return;
  testing.value = true;
  testResult.value = null;
  try {
    const response = await testLilithCustomTool(accountId.value, payload());
    testResult.value = {
      success: true,
      status: response.data?.status,
      body: response.data?.body || '',
    };
  } catch (error) {
    testResult.value = {
      success: false,
      message:
        error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.TOOLS.TEST_ERROR'),
    };
  } finally {
    testing.value = false;
  }
};

const destroyTool = async tool => {
  if (!props.canManage || saving.value) return;
  const id = String(tool.id || '');
  if (deleteArmed.value !== id) {
    deleteArmed.value = id;
    return;
  }
  saving.value = true;
  try {
    await deleteLilithCustomTool(accountId.value, id);
    deleteArmed.value = '';
    await load();
    useAlert(t('TEAM_LILITH.TOOLS.DELETED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.TOOLS.ERROR')
    );
  } finally {
    saving.value = false;
  }
};

watch(accountId, load, { immediate: true });
</script>

<template>
  <div class="flex flex-1 overflow-y-auto bg-n-surface-1">
    <div
      class="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-8 lg:px-10"
    >
      <header
        class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
      >
        <div>
          <div
            class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
          >
            {{ $t('TEAM_LILITH.TOOLS.EYEBROW') }}
          </div>
          <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.TOOLS.TITLE') }}
          </h1>
          <p class="mt-2 max-w-3xl text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.TOOLS.DESCRIPTION') }}
          </p>
        </div>
        <Button
          v-if="canManage"
          data-test="add-tool"
          :label="$t('TEAM_LILITH.TOOLS.ADD')"
          icon="i-lucide-plus"
          :disabled="saving || tools.length >= 15"
          @click="openCreate"
        />
      </header>

      <div
        v-if="softLimitWarning"
        data-test="soft-limit-warning"
        class="rounded-lg border border-n-amber-5 bg-n-amber-2 px-4 py-3 text-sm text-n-amber-11"
      >
        {{ $t('TEAM_LILITH.TOOLS.SOFT_LIMIT_WARNING') }}
      </div>

      <section
        v-if="formOpen"
        data-test="tool-form"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div class="mb-4 flex items-center justify-between gap-4">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{
              formMode === 'edit'
                ? $t('TEAM_LILITH.TOOLS.EDIT')
                : $t('TEAM_LILITH.TOOLS.CREATE')
            }}
          </h2>
          <span class="text-xs text-n-slate-10">
            {{ $t('TEAM_LILITH.TOOLS.TITLE_LIMIT') }}
          </span>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('TEAM_LILITH.TOOLS.NAME') }}
            <input
              v-model="form.title"
              data-test="tool-title"
              maxlength="55"
              class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
            />
          </label>

          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('TEAM_LILITH.TOOLS.METHOD') }}
            <select
              v-model="form.httpMethod"
              data-test="tool-method"
              class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
            >
              <option
                v-for="method in HTTP_METHODS"
                :key="method"
                :value="method"
              >
                {{ method }}
              </option>
            </select>
          </label>

          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            {{ $t('TEAM_LILITH.TOOLS.DESCRIPTION_LABEL') }}
            <textarea
              v-model="form.description"
              data-test="tool-description"
              rows="2"
              class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
            />
          </label>

          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            {{ $t('TEAM_LILITH.TOOLS.ENDPOINT') }}
            <input
              v-model="form.endpointUrl"
              data-test="tool-endpoint"
              :placeholder="ENDPOINT_PLACEHOLDER"
              class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 font-mono text-sm"
            />
          </label>

          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('TEAM_LILITH.TOOLS.AUTH') }}
            <select
              v-model="form.authType"
              data-test="tool-auth-type"
              class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
            >
              <option value="none">
                {{ $t('TEAM_LILITH.TOOLS.AUTH_NONE') }}
              </option>
              <option value="bearer">
                {{ $t('TEAM_LILITH.TOOLS.AUTH_BEARER') }}
              </option>
              <option value="basic">
                {{ $t('TEAM_LILITH.TOOLS.AUTH_BASIC') }}
              </option>
              <option value="api_key">
                {{ $t('TEAM_LILITH.TOOLS.AUTH_API_KEY') }}
              </option>
            </select>
          </label>

          <label
            class="flex items-end gap-2 pb-2 text-sm font-medium text-n-slate-12"
          >
            <input
              v-model="form.enabled"
              data-test="tool-enabled"
              type="checkbox"
            />
            {{ $t('TEAM_LILITH.TOOLS.ENABLED') }}
          </label>

          <label
            v-if="form.authType === 'bearer'"
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            {{ $t('TEAM_LILITH.TOOLS.BEARER_TOKEN') }}
            <input
              v-model="form.bearerToken"
              data-test="tool-bearer-token"
              type="password"
              autocomplete="off"
              class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
            />
          </label>

          <template v-if="form.authType === 'basic'">
            <label
              class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
            >
              {{ $t('TEAM_LILITH.TOOLS.USERNAME') }}
              <input
                v-model="form.basicUsername"
                data-test="tool-basic-username"
                autocomplete="off"
                class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
              />
            </label>
            <label
              class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
            >
              {{ $t('TEAM_LILITH.TOOLS.PASSWORD') }}
              <input
                v-model="form.basicPassword"
                data-test="tool-basic-password"
                type="password"
                autocomplete="new-password"
                class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
              />
            </label>
          </template>

          <template v-if="form.authType === 'api_key'">
            <label
              class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
            >
              {{ $t('TEAM_LILITH.TOOLS.API_KEY_NAME') }}
              <input
                v-model="form.apiKeyName"
                data-test="tool-api-key-name"
                class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
              />
            </label>
            <label
              class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
            >
              {{ $t('TEAM_LILITH.TOOLS.API_KEY_VALUE') }}
              <input
                v-model="form.apiKeyValue"
                data-test="tool-api-key-value"
                type="password"
                autocomplete="off"
                class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm"
              />
            </label>
          </template>

          <label
            v-if="form.httpMethod === 'POST'"
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            {{ $t('TEAM_LILITH.TOOLS.REQUEST_TEMPLATE') }}
            <textarea
              v-model="form.requestTemplate"
              data-test="tool-request-template"
              rows="4"
              class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 font-mono text-sm"
            />
          </label>

          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            {{ $t('TEAM_LILITH.TOOLS.RESPONSE_TEMPLATE') }}
            <textarea
              v-model="form.responseTemplate"
              data-test="tool-response-template"
              rows="4"
              class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 font-mono text-sm"
            />
          </label>
        </div>

        <div class="mt-5 flex flex-col gap-3">
          <div class="flex items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('TEAM_LILITH.TOOLS.PARAMETERS') }}
              </h3>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ $t('TEAM_LILITH.TOOLS.PARAMETERS_HELP') }}
              </p>
            </div>
            <Button
              data-test="add-param"
              :label="$t('TEAM_LILITH.TOOLS.ADD_PARAMETER')"
              size="sm"
              variant="ghost"
              color="slate"
              icon="i-lucide-plus"
              @click="addParam"
            />
          </div>

          <div
            v-for="(param, index) in form.params"
            :key="index"
            data-test="param-row"
            class="grid gap-2 rounded-lg border border-n-weak bg-n-surface-1 p-3 md:grid-cols-[1fr_10rem_1.5fr_auto_auto]"
          >
            <input
              v-model="param.name"
              :data-test="`param-name-${index}`"
              :placeholder="$t('TEAM_LILITH.TOOLS.PARAM_NAME')"
              class="h-9 rounded-md border border-n-weak bg-n-surface-2 px-2 text-sm"
            />
            <select
              v-model="param.type"
              :data-test="`param-type-${index}`"
              class="h-9 rounded-md border border-n-weak bg-n-surface-2 px-2 text-sm"
            >
              <option v-for="type in PARAM_TYPES" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
            <input
              v-model="param.description"
              :data-test="`param-description-${index}`"
              :placeholder="$t('TEAM_LILITH.TOOLS.PARAM_DESCRIPTION')"
              class="h-9 rounded-md border border-n-weak bg-n-surface-2 px-2 text-sm"
            />
            <label class="flex items-center gap-2 text-xs text-n-slate-11">
              <input v-model="param.required" type="checkbox" />
              {{ $t('TEAM_LILITH.TOOLS.REQUIRED') }}
            </label>
            <Button
              :data-test="`remove-param-${index}`"
              icon="i-lucide-x"
              size="sm"
              variant="ghost"
              color="ruby"
              @click="removeParam(index)"
            />
          </div>
        </div>

        <div class="mt-5 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
          <div class="flex flex-wrap items-center gap-2">
            <Button
              data-test="test-tool"
              :label="$t('TEAM_LILITH.TOOLS.TEST')"
              variant="faded"
              color="slate"
              icon="i-lucide-play"
              :disabled="!canTest"
              :is-loading="testing"
              @click="runTest"
            />
            <span v-if="!canTest && !testing" class="text-xs text-n-slate-10">
              {{ $t('TEAM_LILITH.TOOLS.TEST_HINT') }}
            </span>
          </div>
          <div
            v-if="testResult"
            data-test="test-result"
            class="mt-3 rounded-md px-3 py-2 text-xs"
            :class="
              testResult.success
                ? 'bg-n-teal-2 text-n-teal-11'
                : 'bg-n-ruby-2 text-n-ruby-11'
            "
          >
            <span v-if="testResult.success">
              {{
                $t('TEAM_LILITH.TOOLS.TEST_SUCCESS', {
                  status: testResult.status,
                })
              }}
              <span v-if="testResult.body"> · {{ testResult.body }}</span>
            </span>
            <span v-else>{{ testResult.message }}</span>
          </div>
        </div>

        <div class="mt-5 flex justify-end gap-2">
          <Button
            :label="$t('TEAM_LILITH.TOOLS.CANCEL')"
            variant="ghost"
            color="slate"
            :disabled="saving || testing"
            @click="closeForm"
          />
          <Button
            data-test="save-tool"
            :label="$t('TEAM_LILITH.TOOLS.SAVE')"
            :disabled="!formValid || atHardLimit || saving || testing"
            :is-loading="saving"
            @click="save"
          />
        </div>
        <p v-if="atHardLimit" class="mt-2 text-right text-xs text-n-ruby-10">
          {{ $t('TEAM_LILITH.TOOLS.HARD_LIMIT') }}
        </p>
      </section>

      <LilithEffectRecovery v-if="canManage" />

      <section class="flex flex-col gap-3">
        <div
          v-if="loading"
          class="rounded-xl border border-n-weak bg-n-surface-2 p-6 text-sm text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.TOOLS.LOADING') }}
        </div>
        <div
          v-else-if="!tools.length"
          data-test="tools-empty"
          class="rounded-xl border border-n-weak bg-n-surface-2 p-6 text-sm text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.TOOLS.EMPTY') }}
        </div>
        <template v-else>
          <article
            v-for="tool in tools"
            :key="tool.id"
            data-test="tool-card"
            class="rounded-xl border border-n-weak bg-n-surface-2 p-4"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <h2 class="truncate text-sm font-semibold text-n-slate-12">
                    {{ tool.title }}
                  </h2>
                  <span
                    class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-mono text-n-slate-10"
                  >
                    {{ tool.http_method }}
                  </span>
                  <span
                    class="rounded-full px-2 py-0.5 text-xs"
                    :class="
                      tool.enabled
                        ? 'bg-n-teal-2 text-n-teal-11'
                        : 'bg-n-alpha-2 text-n-slate-10'
                    "
                  >
                    {{
                      tool.enabled
                        ? $t('TEAM_LILITH.TOOLS.STATUS_ENABLED')
                        : $t('TEAM_LILITH.TOOLS.STATUS_DISABLED')
                    }}
                  </span>
                </div>
                <p v-if="tool.description" class="mt-1 text-sm text-n-slate-11">
                  {{ tool.description }}
                </p>
                <div class="mt-2 break-all font-mono text-xs text-n-slate-10">
                  {{ tool.endpoint_url }}
                </div>
                <div
                  class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-10"
                >
                  <span>{{ tool.slug }}</span>
                  <span v-if="tool.auth_type !== 'none'">
                    {{
                      $t('TEAM_LILITH.TOOLS.AUTH_LABEL', {
                        type: tool.auth_type,
                      })
                    }}
                  </span>
                  <span>
                    {{
                      $t('TEAM_LILITH.TOOLS.PARAM_COUNT', {
                        count: (tool.param_schema || []).length,
                      })
                    }}
                  </span>
                </div>
              </div>
              <div v-if="canManage" class="flex shrink-0 gap-2">
                <button
                  type="button"
                  role="switch"
                  :aria-checked="tool.enabled"
                  :aria-label="tool.title || tool.slug"
                  :disabled="saving"
                  class="rounded border border-n-weak px-3 py-1 text-xs"
                  @click="toggle(tool)"
                >
                  {{
                    $t(
                      tool.enabled
                        ? 'TEAM_LILITH.TOOLS.STATUS_ENABLED'
                        : 'TEAM_LILITH.TOOLS.STATUS_DISABLED'
                    )
                  }}
                </button>
                <Button
                  data-test="edit-tool"
                  :label="$t('TEAM_LILITH.TOOLS.EDIT')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="openEdit(tool)"
                />
                <Button
                  data-test="delete-tool"
                  :label="
                    deleteArmed === String(tool.id)
                      ? $t('TEAM_LILITH.TOOLS.CONFIRM_DELETE')
                      : $t('TEAM_LILITH.TOOLS.DELETE')
                  "
                  size="sm"
                  :variant="deleteArmed === String(tool.id) ? 'solid' : 'ghost'"
                  color="ruby"
                  :disabled="saving"
                  @click="destroyTool(tool)"
                />
              </div>
            </div>
          </article>
        </template>
      </section>
    </div>
  </div>
</template>
